import React, { useContext, useEffect } from 'react';
import styles from './TermsAndConditions.module.scss';
import KalpStudio from '../../assets/kalp-studio.png';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData: user } = useContext(AuthContext);

  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, location, navigate]);

  return (
    <div className={styles.termsContainer}>
      <div className={styles.logoContainer}>
        {/* <img
          src={KalpStudio}
          className={styles.kalpStudioLogo}
          alt="kalpstudio"
        /> */}
      </div>
      <h1 className={styles.mainHeading}>Mai Cash - Terms and Conditions</h1>

      <div className={styles.contentWrapper}>
        <p className={styles.introParagraph}>
          Mai Cash (the "Platform") is a closed-loop digital employee benefit
          and redemption system made available by Mai Labs Private Limited
          ("Mai", the "Company", "we", "us", or "our") for use by the Company's
          authorized users, including employees and approved vendors/service
          providers (where enabled) who are invited or registered by the Company
          to access and use the Platform.
        </p>

        <p className={styles.introParagraph}>
          These Terms and Conditions ("Terms") govern access to and use of the
          Platform by such authorized users and set out the rights,
          responsibilities, and restrictions applicable to such use.
        </p>

        <p className={styles.introParagraph}>
          By accessing or using Mai Cash, you represent and confirm that you are
          an authorized user of the Company, that you have read and understood
          these Terms, and that you agree to be bound by them, as updated from
          time to time. If you do not agree to these Terms, or if you cease to
          be an authorized user of the Company, you must immediately discontinue
          access to and use of the Platform.
        </p>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>1. Definitions</strong>
          </p>

          <p className={styles.paragraph}>
            For purposes of these Terms and Conditions ("Terms"), the following
            capitalized terms have the meanings assigned below:
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.1. "Platform" or "Mai Cash" means the closed-loop digital
              employee benefit and redemption system made available by the
              Company for use by its authorized user.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.2. "Company" means Mai Labs Private Limited.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.3. "Finance/HR Manager" means an Employee of the Company with
              delegated administrative duties on the Platform (for example,
              helping to manage Token distribution or approvals under the
              Organization Admin's oversight).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.4. "Employee" means a current employee of the Company (or such
              other individual authorized by the Company) who is
              invited/registered to use the Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.5. "Vendor" means a third-party service provider, contractor or
              business partner approved by the Company to accept Tokens as
              internal payment and, where enabled, to request settlement of
              Tokens through a Cash-Out Request, subject to Company approval.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.6. "Token" means a closed-loop, contractual unit of account
              recorded on the Platform and made available by the Company for
              limited programme purposes. Tokens are not money, legal tender, a
              deposit, or a stored-value wallet balance, and (for Employees) are
              not redeemable for cash.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.7. "Wallet" means the Platform ledger account associated with a
              User that records Token balance and transaction history.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.8. "Cash-Out Request" means a request raised by a Vendor through
              the Platform to redeem a specified Token amount, which is reviewed
              and either approved or rejected by the Company. Upon approval, the
              corresponding Tokens are burned/deducted on the Platform and the
              Company settles the corresponding amount to the Vendor outside the
              Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.9. "Intellectual Property" means all proprietary rights in the
              Platform and its content, including all software, code, designs,
              text, graphics, logos, trademarks, and related documentation. All
              such Intellectual Property is owned by the Company or its
              licensors (including any provided by the Organization, such as its
              logos). No rights are transferred to Users by virtue of these
              Terms.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.10. "Gift Card" means a prepaid gift voucher, gift card, or
              voucher code that may be made available through the Platform for
              redemption against the goods/services of a third-party
              merchant/brand ("Merchant"), and that is fulfilled through the
              Gift Card Partner and issued by the Merchant (not by the Company).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.11. "Gift Card Partner" means a third-party fulfilment/reseller
              partner engaged by the Company to facilitate Gift Card fulfilment,
              including any replacement/additional partner engaged from time to
              time.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              1.12. "Merchant" means the third-party brand or service provider
              that issues and honours a Gift Card for redemption of its goods or
              services. The Merchant determines the applicable Gift Card terms
              (including validity and redemption rules) and is independent of
              the Company. The Company does not control, endorse, or assume
              responsibility for the Merchant's goods, services, or actions.
            </p>
          </div>

          <p className={styles.paragraph}>
            Capitalized terms not defined above shall have the meanings given
            elsewhere in these Terms.
          </p>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              2. Scope of Service and Acceptance
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              2.1. <strong>Internal Use Only - Acceptance of Terms:</strong>{' '}
              These Terms govern your access to and use of the Platform. The
              Platform is made available by the Company for internal use by its
              employees only, and access is provided at the Company's
              discretion. By accessing or using the Platform, you confirm that
              you are an authorized employee invited or registered by the
              Company and that you have read, understood, and agree to be bound
              by these Terms, as updated from time to time. If you do not agree
              to these Terms, or if you cease to be an authorized employee of
              the Company, you must not use the Platform and must immediately
              discontinue access. Use of the Platform is voluntary and provided
              as a convenience; no fees are charged to you for basic use of the
              Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              2.2. <strong>Closed-Loop System - No Public Access:</strong> The
              Platform is a closed-loop system and is not available for public
              use. Any tokens, points, credits, or similar units ("Tokens") made
              available through the Platform may be used only within the
              Platform and only for permitted redemption workflows made
              available from time to time, including the purchase or redemption
              of eligible gift cards (where enabled).
            </p>
            <p className={styles.paragraph}>
              "user-to-user transfers" means transfers between Employees.
              Employees may not transfer Tokens to other Employees. Transfers or
              redemptions involving Vendors (including Employee payments to
              Vendors and Vendor cash-out requests, where enabled) are not
              user-to-user transfers and are permitted only through the
              Platform's official workflows. Any attempted transfer outside such
              workflows is void and may result in suspension or termination.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              2.3. <strong>No Agency or New Employment Relationship:</strong>{' '}
              Your use of the Platform is ancillary to your existing employment
              with the Company. Nothing in these Terms creates any agency,
              partnership, joint venture, fiduciary relationship, or other legal
              relationship between you and the Company beyond your existing
              employment relationship. The Platform is provided for convenience
              and may be suspended or terminated as described in Clause 11. If
              you are no longer an authorized employee of the Company, or if you
              do not accept these Terms at any time, you must immediately stop
              using the Platform.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              3. Eligibility and User Accounts
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              3.1. <strong>Authorized Users & Minimum Age:</strong> The Platform
              is available only to individuals explicitly authorized by the
              Company. There is no public sign-up. You may use Mai Cash only if
              you are either:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                a current Employee of the Company (or its affiliate, as
                authorized) whose official email address is registered by the
                Company on the Platform; or
              </li>
              <li className={styles.listItem}>
                a Vendor or service provider that the Company has invited or
                approved to use the Platform. You must be at least 18 years of
                age and legally capable of entering into contracts.
              </li>
            </ol>
            <p className={styles.paragraph}>
              By using the Platform, you represent and warrant that you meet
              these eligibility criteria. Use of the Platform by any
              unauthorized person is strictly prohibited. If, in exceptional
              cases, the Company permits an individual under 18 (e.g., an
              intern) to use the Platform, it does so under its responsibility
              and must supervise that user's compliance with these Terms.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              3.2. <strong>Registration and Authentication:</strong> Employees
              and Vendors may access the Platform only after being added and
              enabled by the Company (through authorized administrators).
              Employees authenticate using their registered email address and a
              one-time password ("OTP") delivered to that email address. Vendors
              authenticate using their registered email address and an OTP
              delivered to that email address (and any additional authentication
              factors the Company may enable). The Company is responsible for
              the accuracy of the email addresses it registers and for
              maintaining joiner–mover–leaver controls, including prompt
              deactivation upon cessation of employment or withdrawal of Vendor
              authorization.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              3.3. <strong>Account Security:</strong> You must keep OTPs and any
              access credentials confidential and must not permit any other
              person to access or use your account. If you suspect that your
              account has been compromised or used without authorization, you
              must promptly notify the Company at hello@mai.io and follow any
              reasonable instructions provided for securing your account. You
              are responsible for activity conducted through your account until
              you notify the Company of suspected unauthorized access. To the
              maximum extent permitted by applicable law, the Company is not
              responsible for losses arising from your failure to safeguard OTPs
              or other authentication factors.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              3.4. <strong>Use of Account - Compliance and Monitoring:</strong>{' '}
              You must use the Platform only for lawful purposes and in
              accordance with these Terms and any applicable Company policies.
              The Company may monitor, review, and audit Platform usage for
              compliance, fraud prevention, security, and system integrity, in
              accordance with applicable law. Misuse may result in suspension or
              termination of access under Clause 11, in addition to any other
              actions available to the Company under applicable law and Company
              policies.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              4. License to Use and Intellectual Property
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              4.1. <strong>Limited License:</strong> Subject to your compliance
              with these Terms, the Company grants you a limited, non-exclusive,
              non-transferable, revocable license to access and use the Platform
              through the Company-authorized web or mobile interfaces solely for
              your internal use as an authorized employee of the Company and
              only for the Platform's intended closed-loop functions, which may
              include:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                viewing your Token balance and transaction history; and
              </li>
              <li className={styles.listItem}>
                redeeming Tokens for eligible gift cards and/or other redemption
                options made available on the Platform from time to time (where
                enabled).
              </li>
            </ol>
            <p className={styles.paragraph}>
              No cash withdrawal, cash-out, or user-to-user transfer of Tokens
              is permitted except as expressly enabled within the Platform's
              official workflows.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              4.2. <strong>Personal Access - No Transfer:</strong> Your account
              and the rights granted to you under these Terms are personal to
              you. You must not rent, lease, sublicense, assign, transfer, or
              otherwise make your account or access rights available to any
              other person. Sharing access credentials, permitting another
              person to use your account, or impersonating another user is
              prohibited and may result in immediate suspension or termination
              of access. If you cease to be an authorized employee of the
              Company, your license to use the Platform terminates
              automatically.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              4.3. <strong>Intellectual Property Ownership:</strong> As between
              you and the Company, the Platform (including all software,
              interfaces, design, text, graphics, workflows, and other
              components) and all intellectual property rights therein are and
              shall remain the exclusive property of the Company and/or its
              licensors. Except for the limited license expressly granted in
              these Terms, nothing in these Terms transfers any right, title, or
              interest in or to the Platform or any intellectual property rights
              to you. You are not granted any right to use the Company's names,
              logos, or trademarks except as displayed within the Platform for
              permitted use.
            </p>
            <p className={styles.paragraph}>
              Technology services for the Platform may be provided to the
              Company by third-party service providers. Such service providers
              are not parties to these Terms with you.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              4.4. <strong>Use Restrictions:</strong> You must not (and must not
              attempt to):
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                copy, modify, adapt, translate, or create derivative works of
                the Platform or any part of it;
              </li>
              <li className={styles.listItem}>
                reverse engineer, decompile, disassemble, or attempt to extract
                source code or underlying ideas of the Platform except to the
                extent such restriction is prohibited by applicable law;
              </li>
              <li className={styles.listItem}>
                remove, obscure, or alter proprietary notices on the Platform;
              </li>
              <li className={styles.listItem}>
                access or use the Platform in a manner that interferes with,
                disrupts, damages, or degrades the Platform or any related
                systems;
              </li>
              <li className={styles.listItem}>
                attempt to gain unauthorized access to any portion of the
                Platform or its related systems (including through hacking,
                scanning, or circumvention of access controls); or
              </li>
              <li className={styles.listItem}>
                use the Platform to engage in fraud, misuse, or unlawful
                activity.
              </li>
            </ol>
            <p className={styles.paragraph}>
              Any breach of this Clause may result in immediate revocation of
              your license and suspension or termination of access under Clause
              11, without limiting the Company's other rights or remedies.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              4.5. <strong>Feedback:</strong> If you provide any suggestions,
              ideas, feedback, or recommendations regarding the Platform
              ("Feedback"), you grant the Company a perpetual, irrevocable,
              worldwide, royalty-free, sublicensable license to use, reproduce,
              modify, adapt, implement, and otherwise exploit such Feedback for
              any purpose, without compensation or obligation to you. You agree
              not to provide Feedback that infringes any third-party rights or
              that would impose any confidentiality, attribution, or other
              obligations on the Company.
            </p>
            <p className={styles.paragraph}>
              Gift card fulfilment (where enabled) may be provided through
              third-party partners. Your purchase and use of any gift card may
              be subject to the applicable issuer/merchant and partner terms,
              and the Company does not issue gift cards or assume
              issuer/merchant obligations.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              5. Acceptable Use and Conduct
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.1. <strong>Compliance with Laws and Policies:</strong> You must
              use the Platform lawfully, responsibly, and with integrity. You
              must comply with all applicable laws, rules, and regulations
              ("Applicable Law") and all applicable Company policies while using
              the Platform, including policies relating to information security,
              acceptable IT use, anti-fraud, anti-bribery and corruption, and
              codes of conduct. Any illegal use of the Platform, or use in
              breach of Applicable Law or Company policies, is prohibited.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.2. <strong>Security and Access Restrictions:</strong> You must
              not attempt to circumvent, disable, or interfere with any security
              features or access controls of the Platform. Prohibited conduct
              includes
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                probing, scanning, or testing the vulnerability of the Platform;
              </li>
              <li className={styles.listItem}>
                attempting to breach authentication or encryption measures;
              </li>
              <li className={styles.listItem}>
                accessing or using any portion of the Platform that you are not
                authorized to access; or
              </li>
              <li className={styles.listItem}>
                using automated scripts, scraping tools, bots, or similar means
                to access the Platform without the Company's prior written
                authorization.
              </li>
            </ol>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.3. <strong>No Malware or Harmful Code:</strong> You must not
              introduce any viruses, malware, spyware, worms, Trojan horses, or
              other malicious or harmful code into the Platform. You must not
              use any device, software, or routine to interfere with the proper
              working of the Platform or the security of the Platform or related
              systems.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.4.{' '}
              <strong>Data Input Restrictions (No Content Hosting):</strong> The
              Platform is not intended for public posting, messaging, or
              user-generated content hosting. You must not input, submit, or
              store through the Platform any unlawful, infringing, defamatory,
              obscene, or otherwise harmful content, or any content that
              violates third-party rights. You must provide only the information
              strictly necessary to use the Platform and complete its workflows.
              The Company may restrict, remove, or disable access to data that
              violates this Clause 5.4 or Applicable Law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.5. <strong>Fraudulent or Unlawful Activities:</strong> You must
              not use the Platform for any fraudulent, deceptive, or unlawful
              activity. Prohibited conduct includes misrepresenting your
              identity or role, impersonating others, attempting to manipulate
              balances or records, and using the Platform in connection with
              money laundering, terrorist financing, bribery, or other financial
              crime. Any suspected misuse may result in immediate suspension or
              termination of access under Clause 11 and may be reported to law
              enforcement or relevant authorities where required or permitted by
              Applicable Law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.6. <strong>No Unauthorized Token Dealings:</strong> Tokens are
              intended solely for closed-loop use within the Platform. You must
              not sell, trade, transfer, pledge, or exchange Tokens for cash or
              other value, and you must not attempt to move Tokens outside the
              Platform or treat Tokens as a publicly tradable asset. No
              user-to-user transfer of Tokens is permitted except as expressly
              enabled within the Platform's official workflows. Any attempted
              circumvention is void and constitutes a breach of these Terms.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.7. <strong>Integrity of Platform Use:</strong> You must not
              engage in conduct that disrupts, harms, or degrades the Platform's
              normal operation, security, or reputation. This includes excessive
              or abusive usage that overwhelms infrastructure, attempts to
              bypass rate limits or controls, and abusive, harassing, or
              offensive conduct in connection with Platform use. You must comply
              with any reasonable usage guidelines communicated by the Company
              from time to time.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              5.8. <strong>Consequences of Violation:</strong> Any violation of
              this Clause 5 may result in suspension or termination of your
              account and access to the Platform under Clause 11, without
              limiting any other rights or remedies available to the Company
              under Applicable Law or Company policy, including disciplinary
              action where applicable.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              6. Platform Functionality and Tokens
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.1. <strong>Nature of Tokens:</strong> Tokens are stable-value
              digital units recorded on the Platform and used solely within Mai
              Cash for the Company's internal, closed-loop program. Tokens may
              be referenced to a fiat currency for internal accounting and
              display purposes only. Tokens are not legal tender, are not
              publicly offered, and may not be transferred, traded, or used
              outside the Platform. Subject to these Terms, Employees may use
              Tokens only to:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                pay registered Vendors within the Platform via the Pay-to-Vendor
                QR workflow (where enabled); and/or
              </li>
              <li className={styles.listItem}>
                purchase Gift Cards (where enabled). Vendors may request
                redemption by submitting a Cash-Out Request under Clause 8,
                subject to the Company's approval.
              </li>
            </ol>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.2. <strong>Token Denominations and Rounding Policy:</strong> The
              Platform records Tokens in whole-unit denominations unless the
              Platform expressly supports sub-units. Where the Platform
              processes a transaction in whole Tokens and a calculation results
              in a fractional amount, the Platform will round to the nearest
              whole Token for purposes of debiting/crediting your balance and
              recording the transaction (fractions below 0.5 rounded down;
              fractions equal to or above 0.5 rounded up). The rounded Token
              amount will be the final amount reflected in the Platform ledger
              and transaction history. You must maintain a sufficient Token
              balance to complete the transaction as rounded.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.3. <strong>Company Administrative Operations:</strong> Token
              issuance and administrative Token operations are performed only by
              the Company (through authorized administrators). The Company may
              mint Tokens, burn Tokens, and allocate/transfer Tokens to
              Employees through the Platform, and the Platform will record such
              actions and the Token history within the administrative interface.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.4. <strong>Issuance and Transfer of Tokens:</strong> Tokens are
              issued ("minted") by the Company and allocated to Users through
              the Platform. Tokens may be transferred only through the
              Platform's official workflows, including (as applicable) Company
              allocation to Employees and Employee payments to registered
              Vendors by scanning the Vendor's QR code (where enabled). Employee
              payments to registered Vendors via QR will be processed only if
              the Employee has a sufficient Token balance at the time of
              payment; otherwise, the transaction may fail or be declined. No
              other user-to-user Token transfers are supported or permitted.
              Without limitation:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                Employees may not transfer Tokens to other Employees;
              </li>
              <li className={styles.listItem}>
                Vendors may not transfer Tokens to any User (including Employees
                or other Vendors); and
              </li>
              <li className={styles.listItem}>
                Employee→Vendor transfer is permitted only via the Pay-to-Vendor
                QR workflow to registered Vendors.
              </li>
            </ol>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.5. <strong>Redemption Requests (Cash-Out):</strong> The Platform
              allows Vendors to request redemption of Tokens through the
              Platform's cash-out feature by submitting a Cash-Out Request (see
              Clause 8). The Platform does not disburse cash or hold fiat funds
              for Users. Upon approval of a Cash-Out Request, the corresponding
              Tokens are burned on the Platform and the Company makes settlement
              to the Vendor outside the Platform using the Company's own payment
              processes.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.6. <strong>Fees and Third-Party Charges:</strong> As of the
              Effective Date, the Company does not charge you any fee for
              standard Platform operations. Gift card fulfilment (where enabled)
              may be provided through third-party partners and may be subject to
              third-party terms, conditions, and fees (if any) imposed by such
              partners, issuers, or merchants. If the Company introduces any
              user-facing Platform fee in the future, you will be notified in
              accordance with Clause 15.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.7. <strong>Balance Review; Error Reporting:</strong> You may
              view your Token balance and transaction history through the
              Platform. You are responsible for reviewing your account activity
              and promptly reporting any suspected discrepancy or unauthorized
              transaction to the Company at hello@mai.io.
            </p>
            <p className={styles.paragraph}>
              The Company will use reasonable efforts to investigate and correct
              verifiable errors in the Platform's records. If you unreasonably
              delay reporting an issue and that delay materially impairs
              investigation or remediation, your ability to obtain a correction
              may be affected to the extent permitted by applicable law. You
              should retain your own records (such as confirmations) of
              significant transactions.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.8. <strong>Misuse and Fraud Controls:</strong> Fraudulent or
              unauthorized use of Tokens or attempts to manipulate Platform
              records are prohibited. This includes attempting to obtain Tokens
              by deception, exploiting bugs or vulnerabilities to increase
              balances, or otherwise circumventing Platform controls. The
              Company may implement monitoring and fraud-detection measures and
              may take corrective action where misuse is suspected, including
              freezing access, reversing transactions, cancelling Tokens
              wrongfully obtained, adjusting balances, and reporting incidents
              to law enforcement or competent authorities where required or
              permitted by applicable law. Users engaged in Token-related misuse
              may have access suspended or terminated under Clause 11, and the
              Company may pursue recovery of any undue benefit to the extent
              permitted by applicable law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              6.9.{' '}
              <strong>Expiration; Forfeiture; Withdrawal of Access:</strong>{' '}
              Tokens may expire, be cancelled, or be withdrawn in accordance
              with the Company's program rules and/or as required by applicable
              law, including upon your loss of authorized access to the
              Platform. Where required by applicable law, the Company will
              provide prior notice of material changes relating to Token expiry
              or cancellation.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              7. Gift Cards and Third-Party Services
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              7.1. <strong>Redemption of Tokens for Gift Cards:</strong> The
              Platform may permit you to redeem Tokens for gift cards or
              vouchers ("Gift Cards") made available through the Platform from
              time to time. To redeem Tokens for a Gift Card, you must have a
              sufficient Token balance at the time of the request. Gift Card
              fulfilment is subject to availability and third-party constraints
              (including issuer/merchant rules, partner limits, or technical
              issues). If a Gift Card request cannot be fulfilled and no Gift
              Card is issued, any Tokens deducted for that failed request will
              be re-credited to your Token balance within a reasonable time. A
              Gift Card request may be declined, delayed, or reversed due to
              factors outside the Company's control, including partner or issuer
              limitations, suspected fraud, compliance checks, or service
              interruptions.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              7.2.{' '}
              <strong>
                Third-Party Terms; Role of Issuers and Fulfilment Partner:
              </strong>{' '}
              Gift Cards available through the Platform are issued by
              third-party merchants/issuers (each, a "Merchant") and may be
              fulfilled and/or delivered through third-party partners engaged by
              the Company, (each, a "Gift Card Partner"). The Company does not
              issue Gift Cards and does not act as the Merchant. The Merchant
              determines the Gift Card's terms and conditions (including
              redemption rules, validity/expiry, and participating outlets), and
              such terms may change from time to time. You are responsible for
              reviewing and complying with the applicable Gift Card terms
              provided at the time of purchase and/or along with the Gift Card.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              7.3. <strong>Token Deduction and Finality:</strong> When you
              successfully redeem Tokens for a Gift Card, the corresponding
              Token amount will be debited from your Token balance at the time
              of issuance (or such other time as reflected in the Platform).
              Gift Card redemptions are generally final and non-refundable once
              issued, except where:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                required by applicable law; or
              </li>
              <li className={styles.listItem}>
                a refund is permitted by the Merchant/Gift Card Partner terms
                and is successfully processed.
              </li>
            </ol>
            <p className={styles.paragraph}>
              If a Gift Card is issued with an error attributable to the
              Merchant or Gift Card Partner (for example, an invalid code), you
              must promptly notify the Company at hello@mai.io. The Company may,
              at its discretion, facilitate coordination with the relevant third
              party; however, any resolution (including replacement or refund)
              is subject to the third party's policies and processes, and the
              Company does not guarantee a specific outcome.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              7.4.{' '}
              <strong>
                Third-Party Services, Data Sharing and No Responsibility for
                Third-Party Acts:
              </strong>{' '}
              The Platform may rely on third-party services (including hosting,
              email delivery, fraud and risk tools, and Gift Card
              Partners/Merchants) to provide certain features. The Company does
              not guarantee uninterrupted availability of third-party services
              and is not responsible for outages, failures, or breaches
              attributable to third parties. To enable Gift Card transactions
              and related support, you authorize the Company to share
              information strictly necessary to process, deliver, and support
              Gift Cards (such as your registered identifiers and transaction
              details) with the relevant Merchant and/or Gift Card Partner, in
              accordance with applicable law and the Company's privacy notice.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              8. Vendor Cash-Out and Settlement
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              8.1. <strong>Initiating a Cash-Out Request:</strong> Only Vendors
              may request redemption of Tokens through the Platform's cash-out
              feature by submitting a Cash-Out Request. To initiate a cash-out,
              the Vendor must submit a Cash-Out Request specifying the number of
              Tokens to redeem (not exceeding the Vendor's available Token
              balance) and each Cash-Out Request is processed individually.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              8.2. <strong>Company Review and Approval:</strong> Each Cash-Out
              Request is subject to review and approval by the Company (through
              authorized administrators). The Company may require the Vendor to
              confirm or update payout particulars and may place a request on
              hold until the Company is reasonably satisfied that the request is
              legitimate, raised by the correct Vendor account, and that payout
              particulars are accurate. The Company may accept or reject
              Cash-Out Requests in accordance with its internal policies and
              applicable arrangements with the Vendor, and the Platform will
              record the status accordingly.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              8.3.{' '}
              <strong>Burning of Tokens and Off-Platform Settlement:</strong>{' '}
              Upon approval of a Cash-Out Request, the Platform will burn the
              approved Token amount (deducting it from the Vendor's balance and
              removing it from circulation on the Platform ledger). Following
              such approval and burn, the Company will settle the corresponding
              amount to the Vendor outside the Platform using the Company's own
              payment processes. The Platform does not process, transmit, or
              disburse fiat currency to Vendors, and does not hold funds on
              behalf of Vendors.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              8.4. <strong>Timing and Status:</strong> The Platform will reflect
              the status of Cash-Out Requests. The timing of actual credit of
              funds to the Vendor depends on the Company's internal approvals.
              Burning of Tokens occurs on approval in the Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              8.5. <strong>Technology Service Provider's Disclaimer:</strong>{' '}
              The Platform's technology, including the Token ledger and workflow
              records, may be provided to the Company by one or more third-party
              technology service providers. Such service providers act solely as
              technology providers and do not hold Tokens and do not participate
              in or control Vendor settlement decisions. All cash-out approvals
              and off-Platform settlement to Vendors are the sole responsibility
              of the Company, using its own payment processes. Any issues
              relating to incorrect payout particulars or settlement disputes
              must be addressed between the Vendor and the Company. The role of
              the technology service provider is limited to maintaining Platform
              records and, where reasonably required, providing technical logs
              or workflow records evidencing Platform actions.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              8.6. <strong>Finality and Disputes:</strong> Vendors acknowledge
              that the Company's decision to approve or reject a Cash-Out
              Request is final for Platform processing purposes. Disputes
              relating to settlement amounts, timing, or payout particulars must
              be raised with the Company at hello@mai.io and will be handled in
              accordance with these Terms and applicable arrangements between
              the Company and the Vendor.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              9. Responsibilities and Obligations
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              9.1.{' '}
              <strong>Company Responsibilities and Internal Controls.</strong>{' '}
              The Company bears primary responsibility for administering the
              Platform and controlling how it is used. The Company will
              implement and maintain reasonable internal controls over Platform
              use, including: managing administrative access and roles; applying
              approval workflows for Token issuance, allocation, and Vendor
              cash-out (where enabled); and reviewing Platform activity for
              irregularities. The Company is responsible for operating the
              Platform in compliance with Applicable Law and for taking
              reasonable steps to prevent misuse of Tokens for unlawful activity
              (including fraud or tax evasion). The Company may delegate
              administrative tasks to authorized personnel or service providers;
              however, the Company remains responsible for actions taken by its
              authorized administrators within the Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              9.2. <strong>Monitoring and Fraud Detection:</strong> The Company
              may monitor Token transactions and Platform usage for signs of
              fraud, misuse, or unusual activity, including through reports,
              logs, and automated detection tools. If the Company detects or
              suspects a security breach or violation of these Terms, it may
              take appropriate action, such as freezing affected accounts,
              halting suspicious transactions, requiring additional
              verification, or investigating internally. Users may report
              suspected unauthorized activity or security concerns to the
              Company at hello@mai.io. While the Platform includes security
              features, users acknowledge that vigilance and timely reporting
              are important for preventing loss or misuse.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              9.3. <strong>User (Employee/Vendor) Responsibilities:</strong>{' '}
              Each user must use the Platform ethically and lawfully, in
              compliance with these Terms and any applicable Company policies
              communicated to you. You must keep sensitive information obtained
              through the Platform confidential and must not misuse such
              information. You are responsible for maintaining the security of
              the devices and accounts used to access the Platform (including
              keeping software updated and avoiding compromised devices). If you
              become aware of any unauthorized access, vulnerability, or error
              affecting the Platform, you must promptly notify the Company and
              must not exploit it. Failure to adhere to these responsibilities
              may result in suspension or termination of access under Clause 11
              and other action as permitted by Applicable Law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              9.4. <strong>Vendor Obligations:</strong> Vendors using the
              Platform agree to:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                use the Platform only for genuine transactions with the Company;
              </li>
              <li className={styles.listItem}>
                provide goods and/or services (if applicable) consistent with
                the terms agreed with the Company; and
              </li>
              <li className={styles.listItem}>
                comply with Applicable Law. Vendors are solely responsible for
                all taxes and statutory obligations applicable to their receipts
                or settlements (including, where applicable, invoicing, GST,
                TDS, and other reporting requirements), and for maintaining
                their own books and records.
              </li>
            </ol>
            <p className={styles.paragraph}>
              Vendors must ensure that payout particulars submitted for cash-out
              are accurate, current, and belong to the Vendor; the Vendor is
              responsible for any losses, delays, or disputes arising from
              incorrect or third-party payout details. Vendors must not engage
              in any off-platform arrangement to sell, trade, or exchange
              Tokens, or accept Tokens for transactions not authorized by the
              Company. Any misuse of the Platform by a Vendor may result in
              immediate suspension or termination of Vendor access and potential
              legal action.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              9.5. <strong>Company's Oversight Rights:</strong> The Company
              reserves the right (but does not assume an obligation) to monitor
              and review Platform activity to maintain security and system
              integrity, enforce these Terms, and comply with Applicable Law. If
              the Company detects activity that appears suspicious or indicates
              a possible security issue, breach, or misuse, the Company may take
              protective measures, including temporarily suspending or limiting
              features, freezing accounts or transactions, or restricting
              access, where reasonably necessary to prevent harm or protect the
              Platform. The Company may act without prior notice in urgent
              circumstances. If the Company receives a lawful request, order, or
              directive from a government authority or regulator, the Company
              will comply as required by Applicable Law and may notify affected
              users to the extent permitted. The Company will not be liable for
              losses resulting solely from good-faith security or compliance
              actions to the extent permitted by Applicable Law.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              10. Compliance with Law and Regulatory Classification
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              10.1. <strong>Closed-Loop Configuration</strong>
            </p>
            <p className={styles.paragraph}>
              The Platform is configured for private use by the Company and its
              authorized users (including Employees, Vendors, and authorized
              administrators). There is no public sign-up.
            </p>
            <p className={styles.paragraph}>
              Tokens are an internal, contractual unit of account created and
              managed by the Company for limited programme purposes on the
              Platform. Tokens are not currency, not legal tender, not a
              deposit, and are not redeemable for cash by Employees.
            </p>
            <p className={styles.paragraph}>
              No peer-to-peer transfers: The Platform does not enable
              peer-to-peer transfers between Employees, and Users cannot
              transfer Tokens to other Users except through the Platform's
              official workflows expressly enabled by the Company (including,
              where enabled, Employee payments to registered Vendors via QR and
              Vendor Cash-Out Requests under Clause 8).
            </p>
            <p className={styles.paragraph}>
              Gift Cards via third parties: Where enabled, Employees may use
              Tokens to obtain Gift Cards supplied through one or more
              third-party fulfilment partners/resellers/aggregators and
              ultimately issued by the relevant merchant/brand. The Company is
              not the issuer of such Gift Cards, and Gift Card issuance and
              redemption are subject to the applicable third-party and/or
              merchant terms.
            </p>
            <p className={styles.paragraph}>
              Regulatory intent: The parties intend that, as configured and used
              under these Terms, the Platform operates as a closed-loop
              programme and is not intended to constitute a "payment system" or
              require authorization as a PPI.
            </p>
            <p className={styles.paragraph}>
              However, regulatory characterization may depend on programme
              design, user access, redemption options, settlement constructs,
              and regulatory interpretation from time to time. The Company may
              restrict, modify, suspend, or discontinue any feature to maintain
              compliance with Applicable Law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              10.2. <strong>Sanctions and Restricted Parties:</strong> Each User
              represents that they are not a prohibited or sanctioned person
              under Applicable Law. Users must not use the Platform in violation
              of applicable sanctions or export control laws. The Company may
              suspend or terminate access, and may restrict transactions, where
              it reasonably believes such action is required for compliance,
              risk management, or in response to lawful requests by competent
              authorities.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              10.3. <strong>Tax Compliance:</strong> Each party is responsible
              for its own tax compliance arising from its activities and
              transactions. The Company is responsible for evaluating and
              complying with any tax obligations relating to Token allocation
              and programme administration (including any withholding or
              reporting obligations, if applicable). Vendors are responsible for
              taxes applicable to their receipts/settlement (including invoicing
              and statutory reporting obligations). Employees are responsible
              for any personal tax reporting obligations applicable to benefits
              they receive. The Company does not provide tax advice.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              10.4.{' '}
              <strong>
                Territorial Use (India; Incidental Access Outside India) -
              </strong>{' '}
              The Platform is intended primarily for use in India. If any User
              accesses the Platform from outside India, that User is responsible
              for complying with Applicable Law applicable to that access/use in
              that location. The Company makes no representation that the
              Platform is appropriate or available for use outside India. These
              Terms remain governed by Indian law as set out in Clause 19.1.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              11. Termination and Suspension
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              11.1. <strong>User Discontinuance / Offboarding:</strong> Use of
              the Platform is voluntary. A User may stop using the Platform at
              any time. Access to the Platform is available only while the User
              remains authorized by the Company. The Company may deactivate an
              account upon cessation of employment (for Employees), cessation of
              engagement/authorization (for Vendors), or withdrawal of
              authorization for any reason. Any Token balance remaining in an
              Employee account after authorization ends is handled in accordance
              with the Company's programme rules (including expiry,
              cancellation, or forfeiture, as applicable). For clarity,
              Employees have no right to any cash payout or cash conversion of
              Tokens.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              11.2. <strong>Suspension or Termination by the Company:</strong>{' '}
              The Company reserves the right to suspend (temporarily disable) or
              terminate (permanently revoke) any User's access to the Platform
              where the Company reasonably determines it is appropriate,
              including due to:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                violation of these Terms or applicable Company policies;
              </li>
              <li className={styles.listItem}>
                the User no longer being eligible or authorized (including
                employment/engagement ending);
              </li>
              <li className={styles.listItem}>
                security concerns, including suspected compromise, fraud,
                misuse, or unlawful activity;
              </li>
              <li className={styles.listItem}>
                compliance with Applicable Law or a lawful request/order from a
                regulator, law enforcement agency, or competent authority; or
              </li>
              <li className={styles.listItem}>
                the Company's decision to discontinue or materially modify the
                Platform or any feature.
              </li>
            </ol>
            <p className={styles.paragraph}>
              Suspensions may be imposed to allow investigation or risk
              mitigation, whereas terminations are final. In urgent cases
              (including security threats or suspected fraud),
              suspension/termination may be immediate and without prior notice.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              11.3. <strong>Notice:</strong> Where reasonably practicable, the
              Company may provide notice of suspension/termination and, where
              appropriate, an opportunity to cure. Immediate action may be taken
              without notice where required for security, fraud prevention,
              legal compliance, or urgent operational reasons.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              11.4. <strong>Effect of Termination:</strong> Upon suspension or
              termination, the User must cease using the Platform and all rights
              to access the Platform end. The User may lose access to Token
              balances, Wallet views, and transaction history, subject to any
              record retention and access rights required by Applicable Law and
              Company policies.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              11.5. <strong>Post-Termination Matters:</strong> The Company may
              retain logs and records to the extent required for security,
              audit, accounting, dispute resolution, or legal compliance. If a
              Vendor had a Cash-Out Request approved prior to termination, the
              Company remains responsible to complete settlement outside the
              Platform in accordance with Clause 8, subject to applicable law
              and any legitimate compliance, fraud-prevention, or dispute holds.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              12. Warranties and Disclaimers
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              12.1. <strong>Platform Provided "AS IS":</strong> The Platform
              (Mai Cash) and all related services are provided on an "as is" and
              "as available" basis, without any express, implied, or statutory
              warranties, except as expressly set out in these Terms. To the
              maximum extent permitted by applicable law, the Company disclaims
              all warranties of any kind in relation to the Platform, including
              any implied warranties of merchantability, fitness for a
              particular purpose, title, and non-infringement. Your use of the
              Platform is at your own risk, and the Company does not guarantee
              that the Platform will meet your requirements or expectations.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              12.2. <strong>No Warranty of Error-Free Operation:</strong> The
              Company does not warrant that the Platform will be uninterrupted,
              timely, secure, or error-free, or that defects will be corrected
              immediately. While the Company uses reasonable efforts to maintain
              a reliable and secure service, the Company does not guarantee that
              the Platform, or any data or information available through it,
              will be accurate, complete, or up to date at all times.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              12.3.{' '}
              <strong>
                Availability, Maintenance and Security Limitations:
              </strong>{' '}
              You acknowledge that the Platform may be unavailable from time to
              time due to maintenance, upgrades, service interruptions, or
              unforeseen technical issues. The Company does not warrant
              continuous availability or instantaneous access. You further
              acknowledge that no system is completely secure and that, despite
              reasonable security measures, the Company cannot guarantee that
              the Platform will be free from vulnerabilities, unauthorized
              access, or malicious interference by third parties. The Company
              will use reasonable efforts to investigate and address material
              security issues that are identified. To the extent permitted by
              applicable law, the Company is not liable for downtime or service
              interruptions, except to the extent caused by the Company's gross
              negligence or wilful misconduct.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              12.4. <strong>Token Value and Redemption Disclaimer:</strong> The
              Company makes no warranty regarding the value, availability, or
              future redeemability of Tokens beyond what is expressly described
              in these Terms and the Platform's official workflows as enabled
              from time to time. Tokens are a discretionary, closed-loop,
              contractual unit of account and are not money, legal tender, or a
              deposit. Employees have no right to convert Tokens into cash and
              may use Tokens only through permitted in-Platform workflows
              (including Gift Cards where enabled, and other options expressly
              enabled by the Company). Vendor cash-out/settlement (where
              enabled) is subject to the Company's review, approval, and
              off-Platform settlement process under Clause 8. The Company does
              not represent that Tokens will be treated by regulators in any
              particular manner.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              12.5. <strong>Third-Party Services and Content:</strong> The
              Platform may integrate with or provide access to third-party
              services and offerings, including Gift Cards issued by third-party
              merchants/brands and fulfilled through third-party partners. The
              Company disclaims all warranties and responsibility relating to
              third-party services, content, tools, merchants, brands, issuers,
              and fulfilment partners, including their availability, quality,
              performance, or the goods/services obtained using them. Any
              third-party terms apply to such services and offerings, and you
              are responsible for reviewing and complying with those terms.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              12.6. <strong>Scope of Implied Warranties:</strong> Some laws do
              not allow the exclusion of certain warranties or the limitation of
              certain rights. To the extent any non-excludable statutory rights
              or warranties apply, nothing in these Terms excludes them.
              However, to the maximum extent permitted by applicable law, the
              Company's responsibility for any such non-excludable warranties is
              limited to the minimum remedies required by law.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              13. Limitation of Liability
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              13.1. <strong>Exclusion of Certain Damages:</strong> To the
              maximum extent permitted by applicable law, the Company, its
              affiliates, and their respective directors, officers, employees,
              contractors, and service providers, shall not be liable to any
              User or any third party for any indirect, incidental, special,
              consequential, exemplary, or punitive damages arising out of or
              relating to the Platform or these Terms, whether arising in
              contract, tort (including negligence), strict liability, or
              otherwise, even if advised of the possibility of such damages.
            </p>
            <p className={styles.paragraph}>
              Excluded damages include, without limitation, loss of profits or
              revenue, loss of business or anticipated savings, loss of data,
              business interruption, reputational harm, or the cost of procuring
              substitute services.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              13.2. <strong>Specific Liability Exclusions:</strong> Without
              limiting Clause 13.1, the Company shall not be liable for any loss
              or damage arising from:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                Token value, policy changes, or programme changes, including any
                change to redemption options, expiry, limits, or discontinuation
                of Tokens or any programme feature;
              </li>
              <li className={styles.listItem}>
                Unauthorized access or misuse of your account or credentials,
                except where such access is directly caused solely by the
                Company's proven gross negligence or wilful misconduct;
              </li>
              <li className={styles.listItem}>
                Third-party acts or failures, including failures of merchants,
                Gift Card issuers, fulfilment partners, payment networks,
                internet service providers, hosting providers, or other external
                systems not controlled by the Company; or
              </li>
              <li className={styles.listItem}>
                User actions or omissions, including incorrect payout details
                provided by Vendors, user error, or failure to follow Platform
                instructions or Company policies.
              </li>
            </ol>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              13.3. <strong>Liability Cap:</strong> To the extent that any
              liability of the Company is not excluded under these Terms and is
              finally established by a court of competent jurisdiction, the
              maximum aggregate liability of the Company arising out of or
              relating to the Platform or these Terms shall be limited to the
              fees actually received by the Company for the Platform in the
              twelve (12) months immediately preceding the event giving rise to
              the claim. Where a User does not pay any fees for access to the
              Platform, the Company's liability to that User shall, to the
              extent permitted by applicable law, be limited to a reasonable
              nominal amount reflecting the nature of the service and the
              absence of direct consideration.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              13.4. <strong>Non-Excludable Liabilities:</strong> Nothing in
              these Terms excludes or limits any liability that cannot be
              lawfully excluded or limited under applicable law, including
              liability for fraud or wilful misconduct, or any statutory rights
              that cannot be waived. Where such liability cannot be excluded, it
              shall be limited to the maximum extent permitted by law.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              13.5. <strong>Basis of Bargain:</strong> The limitations and
              exclusions set out in Clauses 12 and 13 form an essential basis of
              the agreement between you and the Company. You acknowledge that
              the Platform is provided as an internal, closed-loop system and
              that the Company would not provide the Platform without these
              limitations. These limitations apply even if any limited remedy
              fails of its essential purpose.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              14. Indemnification
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              14.1. <strong>User Indemnity:</strong> Each User (Employee or
              Vendor) agrees to indemnify, defend, and hold harmless the
              Company, its affiliates, and their respective directors, officers,
              employees, and agents, and the Company's service providers
              (collectively, the "Indemnified Parties") from and against any and
              all third-party claims, demands, investigations, actions, suits,
              proceedings, and associated losses, liabilities, damages,
              judgments, fines, penalties, costs, and expenses (including
              reasonable attorneys' fees) arising out of or relating to:
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                the User's fraud, wilful misconduct, or misuse of the Platform;
              </li>
              <li className={styles.listItem}>
                the User's violation of these Terms or Applicable Law in
                connection with the Platform; or
              </li>
              <li className={styles.listItem}>
                the User's breach of any representations, warranties, or
                obligations under these Terms.
              </li>
            </ol>
            <p className={styles.paragraph}>
              This indemnity applies to third-party claims (including claims by
              government authorities) to the extent they arise from the conduct
              described above, and includes reasonable costs incurred by an
              Indemnified Party in responding to or defending against such
              claims or investigations.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              14.2. <strong>Indemnification Procedure:</strong> If an
              Indemnified Party (including the Company and its affiliates, and
              the Company's service providers to the extent covered as
              Indemnified Parties under Clause 14.1) receives notice of any
              claim or legal action for which it intends to seek indemnification
              from a User, the Indemnified Party will promptly notify the User.
              Failure to provide prompt notice will reduce the User's
              indemnification obligation only to the extent the delay materially
              prejudices the User's ability to defend the claim. Subject to the
              foregoing, the User will assume control of the defense of the
              claim at the User's expense, using counsel reasonably acceptable
              to the Indemnified Party. The Indemnified Party will provide
              reasonable cooperation in the defense, at the User's expense. The
              User must not settle any claim without the Indemnified Party's
              prior written consent if the settlement
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                imposes any obligation or restriction on an Indemnified Party
                (other than payment fully funded by the User), or
              </li>
              <li className={styles.listItem}>
                includes any admission of liability or wrongdoing by an
                Indemnified Party.
              </li>
            </ol>
            <p className={styles.paragraph}>
              Consent will not be unreasonably withheld where the settlement
              consists solely of payment by the User and includes an
              unconditional release of the Indemnified Parties. If the User
              fails to promptly assume or diligently conduct the defense after
              notice, the Indemnified Party may assume control of the defense
              (without obligation to do so), and the User will remain
              responsible for the reasonable costs and expenses incurred. The
              Indemnified Party may participate in the defense with counsel of
              its choice at its own expense.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              14.3. <strong>Survival of Indemnity:</strong> The obligations in
              this Clause 14 survive any termination or expiration of these
              Terms and any termination of a User's access to the Platform, to
              the extent the underlying events occurred while the User had
              access to or used the Platform.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              15. Modifications to Terms
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              15.1. <strong>Updates and Notice:</strong> The Company may amend
              these Terms from time to time. Where an amendment materially
              affects Users' rights or obligations, the Company will provide
              reasonable notice through the Platform and/or via email, unless a
              shorter notice period is required for legal, security, or
              operational reasons.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              15.2. <strong>Effective Date and Acceptance:</strong> Revised
              Terms will be effective from the "Effective Date" stated in the
              updated Terms. Continued access to or use of the Platform after
              the effective date constitutes acceptance of the revised Terms. If
              you do not agree to the revised Terms, you must discontinue use of
              the Platform and request deactivation by contacting the Company at
              hello@mai.io.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              15.3. <strong>Legal or Regulatory Changes:</strong> Amendments
              required to comply with Applicable Law, regulatory directions, or
              binding orders may be implemented immediately or within the
              timeframe prescribed by such requirements, with notice provided as
              soon as reasonably practicable.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>16. Force Majeure</strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              16.1. <strong>No Liability for Force Majeure Events:</strong> The
              Company will not be liable for any failure or delay in performing
              its obligations under these Terms to the extent such failure or
              delay is caused by events or circumstances beyond the Company's
              reasonable control ("Force Majeure Event"). Force Majeure Events
              include, without limitation: acts of God (flood, earthquake,
              storm), fire, epidemic or pandemic, war, terrorism, civil unrest,
              strikes or labour disputes, power or telecommunications outages,
              failures of internet, cloud, or hosting providers and governmental
              orders or changes in law that materially affect performance.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              16.2. <strong>Effect of Force Majeure:</strong> If a Force Majeure
              Event affects the Platform or the Company's performance under
              these Terms, the Company's affected obligations will be suspended
              for the duration of the Force Majeure Event. The Company will use
              reasonable efforts to mitigate the impact and resume performance
              as soon as reasonably practicable. Users acknowledge that during a
              Force Majeure Event the Platform may be unavailable or limited,
              and delays in Token transactions, Gift Card fulfilment, or Vendor
              settlements may occur without breach of these Terms by the
              Company.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              16.3.{' '}
              <strong>
                Notification and Termination for Extended Force Majeure:
              </strong>{' '}
              Where reasonably practicable, the Company will notify Users
              through the Platform and/or email of a material Force Majeure
              Event affecting access or key functionality. If a Force Majeure
              Event continues for an extended period and materially frustrates
              the Platform's operation (for example, beyond thirty (30) days),
              the Company may suspend or discontinue the Platform (in whole or
              in part) and will provide notice to Users as reasonably
              practicable, subject to Clause 15.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              16.4. <strong>No Breach:</strong> A failure or delay caused by a
              Force Majeure Event will not constitute a breach of these Terms,
              provided the Company uses reasonable efforts to mitigate the
              effects and resume performance when the Force Majeure Event ends.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              17. Miscellaneous Provisions
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.1. <strong>Entire Agreement:</strong> These Terms (together
              with any documents expressly incorporated by reference) constitute
              the entire agreement between you and the Company regarding your
              access to and use of the Platform and supersede all prior
              communications and understandings on that subject. Any separate
              commercial arrangement between the Company and third parties does
              not create rights in favour of any User.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.2. <strong>Severability:</strong> If any provision of these
              Terms is held to be invalid, unlawful, or unenforceable, such
              provision will be enforced to the maximum extent permissible and
              the remaining provisions will remain in full force and effect.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.3. <strong>No Waiver:</strong> No failure or delay by the
              Company in exercising any right under these Terms will operate as
              a waiver of that right. Any waiver must be in writing and signed
              by the Company.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.4. <strong>Assignment:</strong> You may not assign or transfer
              any of your rights or obligations under these Terms without the
              Company's prior written consent. The Company may assign or
              transfer these Terms in connection with a merger, acquisition,
              corporate reorganisation, or transfer of business or assets
              relating to the Platform.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.5. <strong>Relationship of Parties:</strong> Nothing in these
              Terms creates any partnership, joint venture, agency, or
              employment relationship between the Company and any User. Users
              access and use the Platform in their capacity as Employees or
              Vendors (as applicable), and nothing in these Terms creates any
              relationship between Users beyond the Platform's permitted
              workflows.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.6. <strong>Interpretation:</strong> Headings are for
              convenience only and shall not affect interpretation. References
              to laws include amendments and re-enactments. The words
              "including" and "such as" shall be construed without limitation.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.7. <strong>Third-Party Beneficiaries:</strong> Except as
              expressly stated in these Terms, no person other than the Company
              and the relevant User has any right to enforce any provision of
              these Terms. Notwithstanding the foregoing, the Company's
              affiliates, directors, officers, employees, and service providers
              are intended third-party beneficiaries of Clauses that by their
              nature protect them (including disclaimers, limitations of
              liability, and indemnities), and may rely upon and enforce such
              provisions to the extent applicable.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              17.8. <strong>Survival:</strong> Provisions which by their nature
              are intended to survive (including intellectual property,
              disclaimers, limitation of liability, indemnities, dispute
              resolution, and governing law) shall survive termination or expiry
              of these Terms or a User's access to the Platform.
            </p>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              18. Governing Law and Dispute Resolution
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              18.1. <strong>Governing Law:</strong> These Terms, and any
              dispute, controversy, or claim arising out of or relating to these
              Terms or the Platform, shall be governed by and construed in
              accordance with the laws of India, excluding its conflict of laws
              principles. Any mandatory protections available under applicable
              law remain unaffected.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              18.2. <strong>Arbitration:</strong> Any dispute, controversy, or
              claim arising out of or relating to these Terms or the Platform
              that is not resolved amicably shall be finally resolved by
              arbitration in accordance with the Arbitration and Conciliation
              Act, 1996. The arbitration shall be conducted by a sole arbitrator
              appointed by the Company. The seat and venue of arbitration shall
              be New Delhi, India, and the proceedings shall be conducted in
              English. The arbitral award shall be final and binding on the
              parties.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              18.3. <strong>Jurisdiction of Courts :</strong> Subject to Clause
              18.2, the courts at New Delhi, India shall have exclusive
              jurisdiction in respect of
            </p>
            <ol className={styles.orderedList}>
              <li className={styles.listItem}>
                applications for interim or injunctive relief in aid of
                arbitration,
              </li>
              <li className={styles.listItem}>
                enforcement of the arbitral award.
              </li>
            </ol>
          </div>
        </section>

        <section className={styles.section}>
          <p className={styles.paragraph}>
            <strong className={styles.sectionHeading}>
              19. User Support and Grievances
            </strong>
          </p>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              19.1. <strong>Contact Information - Support:</strong> Users may
              submit queries, complaints, or support requests relating to the
              Platform or these Terms by contacting the Company at: hello@mai.io
            </p>
            <p className={styles.paragraph}>
              For formal grievances, Users may contact the Company at:
              hello@mai.io
            </p>
            <p className={styles.paragraph}>
              Updated contact details may be notified through the Platform or
              other official communication channels.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              19.2. <strong>Grievance Redressal Mechanism:</strong> Upon receipt
              of a grievance at the above address (or through any
              Platform-enabled grievance feature, if made available), the
              Company will acknowledge the grievance within a reasonable period
              and take reasonable steps to investigate and resolve it in
              accordance with applicable law and the Company's internal
              procedures. The Company may designate a Grievance Officer or
              authorised personnel for handling grievances, and such details may
              be communicated through the Platform or by email.
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              19.3. <strong>Escalation:</strong> If a User does not receive a
              response within a reasonable timeframe or is dissatisfied with the
              resolution, the User may request escalation by writing to the
              Company at hello@mai.io (or such other escalation channel as the
              Company may notify).
            </p>
          </div>

          <div className={styles.subSection}>
            <p className={styles.paragraph}>
              19.4. <strong>Acceptance:</strong> By accessing or using the
              Platform, each User confirms that the User has read, understood,
              and agrees to be bound by these Terms (as updated from time to
              time). If a User does not agree to these Terms or any update, the
              User must immediately discontinue use of the Platform.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
